import { useCallback, useMemo, useRef } from 'react'
import ReactDatePicker from 'react-datepicker'
import cn from 'classnames'
import { format as fnsFormat, getYear } from 'date-fns'

import config from '../../../config'
import { dateStringToDate } from './date-picker-utils'
import DatePickerInput from './date-picker-input'
import DatePickerHeader from '../common/date/date-picker-header'
import 'react-datepicker/dist/react-datepicker.css'
import '../common/date/date-picker.css'

import s from '../common/date/date-picker.module.scss'

import TimePicker from './time-picker'

const MIN_YEAR = 1900
const MAX_YEARS_FORWARD = 10

const DatePicker = props => {
  const {
    onChange,
    value = '',
    isInvalid,
    isDateOnly,
    name,
    isViewMode = false,
    shouldCloseOnSelect = false,
    minDate,
    maxDate,
    ...datePickerProps
  } = props

  const visualFormat = useMemo(() => {
    if (isDateOnly) return config.dateAndTimeFormats.visualDate

    return `${config.dateAndTimeFormats.visualDate} ${config.dateAndTimeFormats.visualTimeFull}`
  }, [isDateOnly])

  const dateFormat = useMemo(() => {
    return isDateOnly ? config.dateAndTimeFormats.date : config.dateAndTimeFormats.dateAndTime
  }, [isDateOnly])

  const inputClassNames = useMemo(
    () =>
      cn('form-control', {
        'border-danger': isInvalid,
      }),
    [isInvalid],
  )

  const datePickerInputType = useMemo(() => {
    return isDateOnly ? 'date' : 'date-and-time'
  }, [isDateOnly])

  const date = useMemo(() => dateStringToDate(value, dateFormat), [value, dateFormat])

  const resolvedMinDate = useMemo(() => minDate ?? new Date(MIN_YEAR, 0, 1), [minDate])
  const resolvedMaxDate = useMemo(() => maxDate ?? new Date(getYear(new Date()) + MAX_YEARS_FORWARD, 11, 31), [maxDate])

  const datePickerRef = useRef(null)

  const openCalendar = useCallback(() => {
    datePickerRef.current?.setOpen(true)
  }, [])

  if (isViewMode) {
    return <div className={s['date-picker-view']}>{date && fnsFormat(new Date(date), visualFormat)}</div>
  }

  return (
    <div className={s['date-picker-wrapper']}>
      <ReactDatePicker
        ref={datePickerRef}
        renderCustomHeader={headerProps => (
          <DatePickerHeader
            {...headerProps}
            showMonthDropdown={datePickerProps.showMonthDropdown}
            showYearDropdown={datePickerProps.showYearDropdown}
            minDate={resolvedMinDate}
            maxDate={resolvedMaxDate}
          />
        )}
        calendarClassName={s.calendar}
        className={inputClassNames}
        weekDayClassName={() => s['calendar-week-day']}
        dayClassName={() => s['calendar-day']}
        customInput={<DatePickerInput type={datePickerInputType} onCalendarIconClick={openCalendar} />}
        onChange={date => onChange(fnsFormat(date, dateFormat))}
        selected={date}
        showTimeInput={!isDateOnly}
        customTimeInput={<TimePicker />}
        disabledKeyboardNavigation
        autoComplete="off"
        shouldCloseOnSelect={shouldCloseOnSelect}
        dateFormat={visualFormat}
        locale="en"
        name={name}
        minDate={resolvedMinDate}
        maxDate={resolvedMaxDate}
        {...datePickerProps}
      />
    </div>
  )
}

export default DatePicker
